// components/BilderGalerie.js
// Bildgalerie-Komponente für mehrere Bilder pro Stein.
//
// Props:
//   bilder         – Array<{ pfad: string, istHauptbild: boolean }> – gespeicherte Bilder vom Server
//   neueDateien    – Array<File> – neu ausgewählte, noch nicht gespeicherte Bilder (Bearbeitungsmodus)
//   steinId        – string – ID des Steins (für sofortige API-Calls im Bearbeitungsmodus)
//   bearbeitungsModus – boolean – schaltet Bearbeiten-Buttons ein
//   onBilderChange – callback(neuesBilderArray) – informiert Parent über Änderungen am bilder-Array
//   onNeueDateienChange – callback(neuesDateiArray) – informiert Parent über neue Datei-Vorschauen

import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import '../styles/BilderGalerie.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function BilderGalerie({
  bilder = [],
  neueDateien = [],
  steinId = null,
  bearbeitungsModus = false,
  onBilderChange = null,
  onNeueDateienChange = null,
}) {
  // Index des aktuell groß angezeigten Bildes
  // (gespeicherte Bilder zuerst, dann neue Dateien)
  const [aktiverIndex, setAktiverIndex] = useState(0);

  // Blob-URLs für die neuen Datei-Vorschauen
  const [vorschauUrls, setVorschauUrls] = useState([]);

  // Ladezustand für API-Calls
  const [laedt, setLaedt] = useState(false);

  // Gesamtliste: gespeicherte Bilder + neue Dateien als virtuelle Einträge
  const alleEintraege = [
    ...bilder.map(b => ({ typ: 'server', pfad: b.pfad, istHauptbild: b.istHauptbild })),
    ...neueDateien.map((_, i) => ({ typ: 'neu', index: i, istHauptbild: false })),
  ];

  // Blob-URLs für neue Dateien erzeugen und aufräumen
  useEffect(() => {
    const urls = neueDateien.map(f => URL.createObjectURL(f));
    setVorschauUrls(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [neueDateien]);

  // Aktiven Index auf gültigen Wert beschränken wenn sich die Liste ändert
  useEffect(() => {
    if (alleEintraege.length > 0 && aktiverIndex >= alleEintraege.length) {
      setAktiverIndex(Math.max(0, alleEintraege.length - 1));
    }
  }, [alleEintraege.length, aktiverIndex]);

  const zuVorherigemBild = useCallback(() => {
    setAktiverIndex(i => (i - 1 + alleEintraege.length) % alleEintraege.length);
  }, [alleEintraege.length]);

  const zuNaechstemBild = useCallback(() => {
    setAktiverIndex(i => (i + 1) % alleEintraege.length);
  }, [alleEintraege.length]);

  const handleFehler = (e) => {
    e.target.onerror = null;
    e.target.src = '/placeholder.png';
    e.target.alt = 'Bild nicht verfügbar';
    e.target.style.objectFit = 'contain';
  };

  // Hauptbild via API setzen (sofortige Aktion)
  const handleHauptbildSetzen = async (pfad) => {
    if (!steinId || laedt) return;
    setLaedt(true);
    try {
      const res = await API.patch(`/steine/${steinId}/hauptbild`, { pfad });
      if (onBilderChange) onBilderChange(res.data.bilder);
    } catch (err) {
      console.error('Fehler beim Setzen des Hauptbildes:', err);
      alert('Fehler beim Setzen des Hauptbildes.');
    } finally {
      setLaedt(false);
    }
  };

  // Einzelnes gespeichertes Bild via API löschen (sofortige Aktion)
  const handleBildLoeschen = async (pfad) => {
    if (!steinId || laedt) return;
    if (!window.confirm('Dieses Bild wirklich löschen?')) return;
    setLaedt(true);
    try {
      const filename = pfad.replace('uploads/', '');
      const res = await API.delete(`/steine/${steinId}/bilder/${filename}`);
      if (onBilderChange) onBilderChange(res.data.bilder);
      setAktiverIndex(0);
    } catch (err) {
      console.error('Fehler beim Löschen des Bildes:', err);
      alert('Fehler beim Löschen des Bildes.');
    } finally {
      setLaedt(false);
    }
  };

  // Neue (noch nicht gespeicherte) Datei aus der Vorschau entfernen
  const handleNeueDateiEntfernen = (index) => {
    if (!onNeueDateienChange) return;
    const aktualisiert = neueDateien.filter((_, i) => i !== index);
    onNeueDateienChange(aktualisiert);
    setAktiverIndex(prev => Math.min(prev, bilder.length + aktualisiert.length - 1));
  };

  if (alleEintraege.length === 0) {
    return (
      <div className="bilder-galerie bilder-galerie-leer">
        <span>Kein Bild vorhanden</span>
      </div>
    );
  }

  const aktiverEintrag = alleEintraege[aktiverIndex] || alleEintraege[0];

  const aktiveSrc =
    aktiverEintrag.typ === 'neu'
      ? vorschauUrls[aktiverEintrag.index]
      : `${BASE_URL}/${aktiverEintrag.pfad}`;

  const aktivesAlt =
    aktiverEintrag.typ === 'neu' ? 'Vorschau neues Bild' : 'Steinbild';

  return (
    <div className={`bilder-galerie${bearbeitungsModus ? ' bearbeitungs-modus' : ''}`}>
      {/* Hauptbild-Anzeige */}
      <div className="galerie-hauptansicht">
        {alleEintraege.length > 1 && (
          <button
            type="button"
            className="galerie-pfeil galerie-pfeil-links"
            onClick={zuVorherigemBild}
            aria-label="Vorheriges Bild"
            disabled={laedt}
          >
            &#8249;
          </button>
        )}

        <div className="galerie-bild-wrapper">
          <img
            src={aktiveSrc}
            alt={aktivesAlt}
            className="galerie-hauptbild"
            onError={handleFehler}
          />
          {aktiverEintrag.typ === 'server' && aktiverEintrag.istHauptbild && (
            <span className="galerie-hauptbild-badge" title="Hauptbild">&#9733; Hauptbild</span>
          )}
          {aktiverEintrag.typ === 'neu' && (
            <span className="galerie-neu-badge">Neu (noch nicht gespeichert)</span>
          )}
        </div>

        {alleEintraege.length > 1 && (
          <button
            type="button"
            className="galerie-pfeil galerie-pfeil-rechts"
            onClick={zuNaechstemBild}
            aria-label="Nächstes Bild"
            disabled={laedt}
          >
            &#8250;
          </button>
        )}
      </div>

      {/* Aktions-Buttons für aktives Bild im Bearbeitungsmodus */}
      {bearbeitungsModus && (
        <div className="galerie-aktionen">
          {aktiverEintrag.typ === 'server' && !aktiverEintrag.istHauptbild && steinId && (
            <button
              type="button"
              className="btn btn-hauptbild"
              onClick={() => handleHauptbildSetzen(aktiverEintrag.pfad)}
              disabled={laedt}
              title="Als Hauptbild festlegen"
            >
              &#9733; Als Hauptbild
            </button>
          )}
          {aktiverEintrag.typ === 'server' && steinId && (
            <button
              type="button"
              className="btn btn-bild-loeschen"
              onClick={() => handleBildLoeschen(aktiverEintrag.pfad)}
              disabled={laedt}
              title="Dieses Bild löschen"
            >
              &#128465; Bild löschen
            </button>
          )}
          {aktiverEintrag.typ === 'neu' && (
            <button
              type="button"
              className="btn btn-bild-loeschen"
              onClick={() => handleNeueDateiEntfernen(aktiverEintrag.index)}
              disabled={laedt}
              title="Auswahl entfernen"
            >
              &#10005; Auswahl entfernen
            </button>
          )}
        </div>
      )}

      {/* Thumbnail-Streifen */}
      {alleEintraege.length > 1 && (
        <div className="galerie-thumbnails">
          {alleEintraege.map((eintrag, index) => {
            const thumbSrc =
              eintrag.typ === 'neu'
                ? vorschauUrls[eintrag.index]
                : `${BASE_URL}/${eintrag.pfad}`;

            return (
              <button
                key={eintrag.typ === 'neu' ? `neu-${eintrag.index}` : eintrag.pfad}
                type="button"
                className={`galerie-thumbnail-btn${index === aktiverIndex ? ' aktiv' : ''}${eintrag.istHauptbild ? ' ist-hauptbild' : ''}`}
                onClick={() => setAktiverIndex(index)}
                title={eintrag.istHauptbild ? 'Hauptbild' : eintrag.typ === 'neu' ? 'Neu' : 'Bild'}
              >
                <img
                  src={thumbSrc}
                  alt={`Thumbnail ${index + 1}`}
                  onError={handleFehler}
                  loading="lazy"
                />
                {eintrag.istHauptbild && (
                  <span className="thumb-hauptbild-marker" title="Hauptbild">&#9733;</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BilderGalerie;
